/**
 * B4CoachConfigEditor.jsx - Sprint 3
 * 
 * Editor de configuración del Coach IA para una semana específica.
 * Permite habilitar/deshabilitar el coach, configurar frecuencia de evaluación,
 * seleccionar áreas de enfoque y agregar instrucciones personalizadas.
 * 
 * @see PLAN_GROUPCOURSE_BUILDER.md - Sprint 3, Día 13
 */

import React, { useState, useEffect } from 'react';
import { 
  Brain,
  AlertCircle, 
  CheckCircle2,
  Info,
  Sparkles
} from 'lucide-react';

const B4CoachConfigEditor = ({ config, onChange }) => {
  const [localConfig, setLocalConfig] = useState({
    enabled: true,
    evaluationFrequency: 'on-submit',
    focusAreas: [],
    customPromptAdditions: ''
  });

  // Sincronizar con props
  useEffect(() => {
    if (config) {
      setLocalConfig({
        enabled: config.enabled !== undefined ? config.enabled : true,
        evaluationFrequency: config.evaluationFrequency || 'on-submit',
        focusAreas: config.focusAreas || [],
        customPromptAdditions: config.customPromptAdditions || ''
      });
    }
  }, [config]);

  // Opciones de áreas de enfoque
  const focusAreasOptions = [
    { value: 'Metodología', label: 'Metodología', icon: '📐' },
    { value: 'Viabilidad técnica', label: 'Viabilidad técnica', icon: '⚙️' },
    { value: 'Innovación', label: 'Innovación', icon: '💡' },
    { value: 'Impacto social', label: 'Impacto social', icon: '🌍' },
    { value: 'Sostenibilidad', label: 'Sostenibilidad', icon: '♻️' },
    { value: 'Escalabilidad', label: 'Escalabilidad', icon: '📈' }
  ];

  const frequencyOptions = [
    { 
      value: 'on-submit', 
      label: 'Al enviar entregable (automático)',
      description: 'El coach evalúa automáticamente cada vez que el estudiante envía su trabajo'
    },
    { 
      value: 'on-request', 
      label: 'Solo cuando estudiante solicite',
      description: 'El estudiante debe pedir explícitamente feedback del coach'
    },
    { 
      value: 'disabled', 
      label: 'Deshabilitado para esta semana',
      description: 'No habrá evaluación del coach IA'
    }
  ];

  const handleUpdate = (field, value) => {
    const updated = { ...localConfig, [field]: value };
    setLocalConfig(updated);
    onChange(updated);
  };

  const handleToggleEnabled = (enabled) => {
    handleUpdate('enabled', enabled);
  };

  const handleToggleFocusArea = (area) => {
    const newAreas = localConfig.focusAreas.includes(area)
      ? localConfig.focusAreas.filter(a => a !== area)
      : [...localConfig.focusAreas, area];
    handleUpdate('focusAreas', newAreas);
  };

  return (
    <div className="space-y-6">
      {/* Header informativo */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 flex items-start gap-3">
        <Brain className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="font-semibold text-purple-900 mb-1">
            Configuración del Coach IA
          </h3>
          <p className="text-sm text-purple-700">
            El Coach IA evalúa el trabajo de los estudiantes y proporciona feedback pedagógico 
            personalizado basado en las áreas de enfoque que definas.
          </p>
        </div>
      </div>

      {/* Toggle Principal: Habilitar/Deshabilitar */}
      <div className="border border-gray-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            id="coach-enabled"
            checked={localConfig.enabled}
            onChange={(e) => handleToggleEnabled(e.target.checked)}
            className="w-5 h-5 text-purple-600 rounded mt-1"
          />
          <div className="flex-1">
            <label 
              htmlFor="coach-enabled" 
              className="font-semibold text-gray-900 cursor-pointer block"
            >
              Habilitar Coach IA para esta semana
            </label>
            <p className="text-sm text-gray-600 mt-1">
              {localConfig.enabled 
                ? '✓ El coach está activo y evaluará entregas según la configuración'
                : '✗ El coach no evaluará entregas en esta semana'
              }
            </p>
          </div>
        </div>
      </div>

      {/* Configuración detallada (solo si está habilitado) */}
      {localConfig.enabled && (
        <div className="space-y-6 pl-4 border-l-4 border-purple-200">
          {/* Frecuencia de Evaluación */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-3">
              Frecuencia de Evaluación
              <span className="text-red-500 ml-1">*</span>
            </label>
            <div className="space-y-2">
              {frequencyOptions.map(option => (
                <label 
                  key={option.value}
                  className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-all ${
                    localConfig.evaluationFrequency === option.value
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-purple-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="evaluationFrequency"
                    value={option.value}
                    checked={localConfig.evaluationFrequency === option.value}
                    onChange={(e) => handleUpdate('evaluationFrequency', e.target.value)}
                    className="w-4 h-4 text-purple-600 mt-0.5"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{option.label}</div>
                    <div className="text-sm text-gray-600 mt-0.5">{option.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Áreas de Enfoque */}
          {localConfig.evaluationFrequency !== 'disabled' && (
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-3">
                Áreas de Enfoque del Coach
                <span className="text-gray-500 font-normal ml-2">(selecciona múltiples)</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {focusAreasOptions.map(area => (
                  <label 
                    key={area.value}
                    className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-all ${
                      localConfig.focusAreas.includes(area.value)
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={localConfig.focusAreas.includes(area.value)}
                      onChange={() => handleToggleFocusArea(area.value)}
                      className="w-4 h-4 text-purple-600 rounded"
                    />
                    <span className="text-lg">{area.icon}</span>
                    <span className="text-sm font-medium text-gray-900">{area.label}</span>
                  </label>
                ))}
              </div>
              {localConfig.focusAreas.length === 0 && (
                <div className="mt-2 flex items-start gap-2 text-sm text-amber-600">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <p>
                    No hay áreas seleccionadas. El coach evaluará de forma general, 
                    pero es recomendable seleccionar al menos una.
                  </p>
                </div>
              )}
              {localConfig.focusAreas.length > 0 && (
                <div className="mt-2 flex items-start gap-2 text-sm text-green-600">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <p>
                    {localConfig.focusAreas.length} área(s) seleccionada(s). 
                    El coach enfocará su evaluación en estos aspectos.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Instrucciones Personalizadas */}
          {localConfig.evaluationFrequency !== 'disabled' && (
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Instrucciones Adicionales para el Coach IA
                <span className="text-gray-500 font-normal ml-2">(opcional)</span>
              </label>

              {/* Guía de qué escribir */}
              <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                <p className="font-semibold mb-1">¿Qué escribir aquí?</p>
                <p className="mb-2">El coach ya sabe que es un evaluador pedagógico. Usa este campo para darle <strong>contexto de tu curso</strong>, no para redefinir su rol.</p>
                <div className="grid grid-cols-1 gap-1">
                  <p className="text-green-700"><strong>✓ Correcto:</strong> "El curso usa metodología Lean Startup. Los estudiantes están en fase de ideación temprana, aún no deben proponer soluciones."</p>
                  <p className="text-green-700"><strong>✓ Correcto:</strong> "Da feedback motivador, los grupos son de primer año. Penaliza asunciones sin evidencia de cliente real."</p>
                  <p className="text-red-600"><strong>✗ Evitar:</strong> "Eres un experto en..." — el rol ya está definido por el sistema.</p>
                </div>
              </div>

              <div className="mb-2 flex items-start gap-2 text-sm text-blue-600 bg-blue-50 border border-blue-200 rounded-lg p-3">
                <Sparkles className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <p>
                  <strong>Ejemplo para esta semana:</strong> "Estamos en la fase de identificación del problema. Evalúa que el grupo haya definido un público específico y un dolor concreto. Penaliza respuestas genéricas. Tono motivador."
                </p>
              </div>
              <textarea
                value={localConfig.customPromptAdditions}
                onChange={(e) => handleUpdate('customPromptAdditions', e.target.value)}
                placeholder="Contexto del curso, tono deseado, énfasis pedagógico de esta semana... (No redefinir el rol del coach)"
                rows={5}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
              />
              <p className="text-sm text-gray-500 mt-2">
                {localConfig.customPromptAdditions.length} caracteres
              </p>
            </div>
          )}

          {/* Info sobre cómo funciona */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-semibold mb-1">¿Cómo funciona el Coach IA?</p>
              <ul className="list-disc ml-4 space-y-0.5 text-blue-700">
                <li>Analiza el entregable del estudiante según los criterios de B3</li>
                <li>Evalúa con énfasis en las áreas de enfoque seleccionadas</li>
                <li>Genera feedback pedagógico personalizado y constructivo</li>
                <li>Proporciona recomendaciones específicas de mejora</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Resumen de configuración */}
      <div className="border-t pt-4">
        <h4 className="text-sm font-semibold text-gray-900 mb-2">Resumen de Configuración</h4>
        <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-700">Estado:</span>
            <span className={localConfig.enabled ? 'text-green-600 font-semibold' : 'text-gray-500'}>
              {localConfig.enabled ? '✓ Habilitado' : '✗ Deshabilitado'}
            </span>
          </div>
          {localConfig.enabled && (
            <>
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-700">Frecuencia:</span>
                <span className="text-gray-900">
                  {frequencyOptions.find(o => o.value === localConfig.evaluationFrequency)?.label}
                </span>
              </div>
              <div className="flex items-start gap-2">
                <span className="font-medium text-gray-700">Áreas de enfoque:</span>
                <span className="text-gray-900">
                  {localConfig.focusAreas.length > 0 
                    ? localConfig.focusAreas.join(', ')
                    : 'Ninguna (evaluación general)'
                  }
                </span>
              </div>
              <div className="flex items-start gap-2">
                <span className="font-medium text-gray-700">Instrucciones personalizadas:</span>
                <span className="text-gray-900">
                  {localConfig.customPromptAdditions 
                    ? `${localConfig.customPromptAdditions.substring(0, 100)}${localConfig.customPromptAdditions.length > 100 ? '...' : ''}`
                    : 'No definidas'
                  }
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default B4CoachConfigEditor;
